import { stringify } from 'query-string';
import { ImResponse, resolve, messDoorClient } from 'services/api';

export type QuestionReplayStatus = 'unanswered' | 'answered' | 'closedUnanswered' | 'underReview';

export type AnswerOrigin =
  | 'MERCADO_QA_ANSWER_ORIGIN_HUMAN'
  | 'MERCADO_QA_ANSWER_ORIGIN_AI'
  | 'MERCADO_QA_ANSWER_ORIGIN_AI_EDITED';

export interface MercadoItemFAQAnswer {
  status: 'active' | 'disabled';
  createTime: string;
  text: string;
  textTranslate?: string;
  userNick: string;
  answerOrigin: AnswerOrigin;
}

export interface MercadoItemFAQQuestion {
  id: string;
  userId: string;
  storeId: string;
  text: string;
  textTranslate?: string;
  status: QuestionReplayStatus;
  itemId: string;
  createTime: string;
  region: General.UpperCaseRegion;
  itemPic: string;
  answer?: MercadoItemFAQAnswer;
  userNick: string;
  dialogReply?: MercadoItemFAQDialogReply;
}

export interface MercadoItemFAQDialogReply {
  dialogResultId: string;
  replied: boolean;
  candidateText: string;
  candidateTranslated: string;
  replyMode: 'ITEM_AI';
  isProductQuestion: boolean;
  action: '1' | '2';
  actionType: 'REPLY' | 'REPLY_AND_TO_HUMAN';
  displayPayload: string;
  failureReason: string;
  scenarioContext: {
    storeGroupId: string;
    storeGroupName: string;
    llmBasicInfo: {
      llmIntentId: string;
      llmIntentName: string;
      llmPolicyId: string;
      llmPolicyName: string;
      llmSubPolicyId: string;
    };
  };
}

interface MercadoItemSkuInfo {
  skuId: string;
  skuName: string;
  externalShopId: string;
  platform: 'MERCADO';
  skuPic: string;
  price: 3159.99;
  currentStock: string;
  internalItemId: string;
  shopSku: string;
  sellerSku: string;
  sku: string;
  stockFlag: boolean;
  skuStockStatus: string;
  originPrice: number;
}

export interface MercadoFAQItem {
  itemId: string;
  imageUrl: string[];
  currentStock: string;
  itemName: string;
  region: General.UpperCaseRegion;
  skuNum: number;
  isPreOrder: string;
  sale: number;
  salesFromTrade: number;
  shopName: string;
  shopAlias: string;
  internalItemId: string;
  externalItemId: string;
  internalStoreId: string;
  externalShopId: string;
  skuInfo: MercadoItemSkuInfo[];
  itemSku: string;
}
export interface MercadoFAQ {
  item: MercadoFAQItem;
  question: MercadoItemFAQQuestion[];
  // 后端没返回id，前端生成一个uuid，方便本地操作数据
  id: string;
}
export interface QueryMercadoFAQListResponse {
  page: number;
  pageNo: number;
  total: number;
  data: MercadoFAQ[];
}

export interface QueryMercadoFAQListParams {
  storeIds?: string[];
  itemId?: string;
  itemName?: string;
  startTime?: string;
  endTime?: string;
  keyword?: string;
  region?: General.UpperCaseRegion;
  status?: QuestionReplayStatus;

  buyerNick?: string;
  itemKeyword?: string;
  // SPU_ID：商品ID， SELLER_SPU：商品主货号，
  itemType?: 'UNKNOWN' | 'SPU_NAME' | 'SPU_ID' | 'SELLER_SPU' | 'SELLER_SKU';
  buyerId?: string;
  lang?: General.SystemLanguage;
  answerUserIds?: number[];
  answerOrigins?: AnswerOrigin[];
}
export interface QueryMercadoFAQListParamsWithPagination extends QueryMercadoFAQListParams {
  page: number;
  pageNo: number;
  orderBy: string;
}

export function queryMercadoFAQList(params: QueryMercadoFAQListParamsWithPagination) {
  return messDoorClient.get<ImResponse<QueryMercadoFAQListResponse>>('/oversea-qa/question', {
    params: {
      ...params,
      timeZone: -Math.floor(new Date().getTimezoneOffset() / 60),
    },
    paramsSerializer: (params) => stringify(params, { arrayFormat: 'none' }),
  });
}

export function getQuestionCount() {
  return resolve(messDoorClient.get<ImResponse<number>>('/oversea-qa/question/count'));
}

export function answerTheQuestion(questionId: string, text: string, dialogResultId?: string) {
  return messDoorClient.post('/oversea-qa/question/answer', { questionId, text, dialogResultId });
}

export function deleteQuestion(questionId: string) {
  return messDoorClient.delete('/oversea-qa/question', { params: { questionId } });
}

export function getConfig() {
  return resolve(
    messDoorClient.get<ImResponse<{ aiAutoReplyEnabled: boolean }>>('/oversea-qa/config'),
  );
}

export function updateConfig(body: { aiAutoReplyEnabled: boolean }) {
  return messDoorClient.post('/oversea-qa/config', body);
}
